import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import isNil from 'lodash/isNil'
import moment from 'moment'
import { Columns } from 'src/components'
import { DatePicker, TimePicker } from 'src/components/pickers'
import { datePlaceholder, timePlaceholder } from 'src/services'
import { useHandler } from 'src/hooks'
import { Icon, DayIcon, ClockIcon, CalendarIcon } from 'src/components/icons'
import styles from './RawDatetimeInput.scss'

const getUpdatedDate = (date, value, momentProps = []) => {
  if (isNil(date)) {
    return null
  } else {
    const wrappedDate = moment(date)
    const updated = value ? moment(value) : moment()
    momentProps.forEach(p => {
      updated.set(p, wrappedDate[p]())
    })
    return updated.toDate()
  }
}

/**
 * A date input with a time input
 */
const RawDatetimeInput = ({ value, onChange, disabled, disabledDate, className, ...props }) => {
  const disabledHours = useHandler(() =>
    disabledDate ? Array.from(Array(24), (x, i) => i).filter(hour => disabledDate(moment(value).hour(hour))) : []
  )
  const disabledMinutes = useHandler(() =>
    disabledDate ? Array.from(Array(60), (x, i) => i).filter(minute => disabledDate(moment(value).minute(minute))) : []
  )
  const disabledSeconds = useHandler(() =>
    disabledDate ? Array.from(Array(60), (x, i) => i).filter(second => disabledDate(moment(value).second(second))) : []
  )
  return (
    <Columns gap="0" layout="50%" align="baseline" className={classnames(styles.container, className)} {...props}>
      <DatePicker
        value={value}
        onChange={date => onChange(getUpdatedDate(date, value, ['year', 'month', 'date']))}
        disabled={disabled}
        disabledDate={disabledDate}
        allowEmpty={false}
      >
        {({ value }) =>
          value ? (
            <span className={styles.date}>
              <Icon icon={DayIcon} opacity=".25" className={styles.icon} />
              {value}
            </span>
          ) : (
            <Columns align="center" className={styles.placeholder}>
              <span className={styles.date}>
                <Icon icon={DayIcon} opacity=".5" className={styles.icon} />
                <span>{datePlaceholder}</span>
              </span>

              <span className={styles.time}>
                <span>{timePlaceholder}</span>
                <Icon icon={ClockIcon} opacity=".5" className={styles.icon} />
              </span>
            </Columns>
          )
        }
      </DatePicker>
      {value && (
        <span className={styles.input}>
          <TimePicker
            className={styles.time}
            inputIcon={<Icon icon={ClockIcon} opacity=".25" className={styles.icon} />}
            value={moment(value)}
            onChange={time => onChange(getUpdatedDate(time, value, ['hour', 'minute', 'second']))}
            disabledHours={disabledHours}
            disabledMinutes={disabledMinutes}
            disabledSeconds={disabledSeconds}
            placeholder={timePlaceholder}
            allowEmpty={false}
          />
        </span>
      )}
    </Columns>
  )
}

RawDatetimeInput.defaultProps = {
  icon: CalendarIcon,
  color: 'light',
}

RawDatetimeInput.propTypes = {
  /**
   * The date value
   */
  value: PropTypes.any,
  /**
   * Handler being called on each change
   */
  onChange: PropTypes.func,
  /**
   * Whether the input is disabled or not
   */
  disabled: PropTypes.bool,
  /**
   * Whether to disable select of current date
   */
  disabledDate: PropTypes.func,
  /**
   * Optionnal class name of the input
   */
  className: PropTypes.string,
}

export default RawDatetimeInput
